<script setup lang="ts">
import { storeToRefs } from "pinia";
import { useGlobalStore } from "@/stores/global-store";
import { useAppStore } from "@/stores/integration";
import { VaAvatar, VaButton, VaDropdown, VaIcon, VaNavbar } from "vuestic-ui";
import CotaModal from "../modal/StakeModal.vue";
import router from "../../router";
import { useI18n } from "vue-i18n";
import { useUserStore } from "@/stores/user";
import { computed, ref } from "vue";
import OnboardingVideoModal from "@/components/modal/OnboardingVideoModal.vue";
import DepositModal from "@/components/modal/DepositModal.vue";
import WithdrawModal from "@/components/modal/WithdrawModal.vue";

const showVideoModal = ref(false);
const { isSidebarMinimized } = storeToRefs(useGlobalStore());
const appStore = useAppStore();
const userStore = useUserStore();
const { t: $t } = useI18n();

const abrirNovaCota = () => appStore.showBotModal();
const abrirSaldo = () => appStore.showCopyModal();

const logout = () => appStore.logout();
const profile = () => router.push("/profile");
const home = () => router.push("/dashboard");
const abrirSaque = () => (appStore.withdrawModal = true);
const abrirVideoOnboarding = () => {
  showVideoModal.value = true;
};

const fecharVideoOnboarding = () => {
  showVideoModal.value = false;
};

const balance = computed(() => {
  return userStore.user?.balance ?? 0;
});
</script>

<template>
  <VaNavbar
    class="justify-between flex !bg-black w-full text-white px-6 py-2 shadow-sm"
  >
    <div class="w-full flex justify-between items-center">
      <div class="flex items-center space-x-4 min-w-[250px]">
        <VaIcon
          name="menu"
          size="24px"
          class="cursor-pointer text-white md:hidden"
          @click="isSidebarMinimized = !isSidebarMinimized"
        />
        <div class="flex items-center space-x-2 cursor-pointer" @click="home">
          <img src="/image/logo.png" alt="Logo" class="w-12 h-12" />
          <span class="font-semibold text-lg tracking-wide text-white"
            >CASH BROKER</span
          >
        </div>
      </div>

      <div class="hidden md:flex items-center space-x-4">
        <VaButton
          color="#F97316"
          class="text-white font-semibold py-2 px-6 rounded-md"
          @click="abrirSaque"
        >
          {{ $t("navbar.sacar") }}
        </VaButton>
        <VaButton
          color="#F97316"
          class="text-white font-semibold py-2 px-6 rounded-md"
          @click="abrirNovaCota"
        >
          {{ $t("navbar.nova_stake") }}
        </VaButton>
        <VaButton
          color="#0C0E1B"
          class="border border-orange-500 text-white py-2 px-6"
          @click="abrirSaldo"
        >
          {{ $t("navbar.saldo") }}
          <span class="text-orange-400 font-bold ml-1">
            {{
              Number(balance).toLocaleString("en-US", {
                style: "currency",
                currency: "BRL",
              })
            }}
          </span>
        </VaButton>

        <VaDropdown placement="bottom-end" class="z-50">
          <template #anchor>
            <VaAvatar
              src="/image/avatar.png"
              size="42px"
              class="cursor-pointer border bg-[#ffffff] border-black rounded-full"
            />
          </template>

          <div
            class="bg-[#0C0E1B] text-white w-40 rounded-lg shadow-lg overflow-hidden"
          >
            <button
              class="w-full text-left px-4 py-3 hover:bg-[#1A1D2F] transition"
              @click="profile"
            >
              <VaIcon name="person" class="mr-2" />
              Perfil
            </button>
            <button
              class="w-full text-left px-4 py-3 hover:bg-[#1A1D2F] transition"
              @click="abrirVideoOnboarding"
            >
              <VaIcon name="ondemand_video" class="mr-2" />
              Vídeo CEO
            </button>

            <button
              class="w-full text-left px-4 py-3 hover:bg-[#1A1D2F] transition"
              @click="logout"
            >
              <VaIcon name="logout" class="mr-2" />
              {{ $t("navbar.sair") }}
            </button>
          </div>
        </VaDropdown>
      </div>
    </div>
  </VaNavbar>

  <!-- Footer Bar Sofisticada -->
  <div class="fixed bottom-0 left-0 w-full z-50 md:hidden pointer-events-none">
    <!-- Container da barra -->
    <div
      class="relative bg-[#0C1020] border-t border-[#1F2436] h-14 flex items-center justify-between px-8 rounded-t-xl shadow-lg pointer-events-auto"
    >
      <!-- Perfil -->
      <button
        class="flex flex-col items-center text-white text-[11px] font-normal"
        @click="profile"
      >
        <VaIcon name="person" class="w-5 h-5 mb-0.5" />
        <span>Perfil</span>
      </button>

      <!-- Saque -->
      <button
        class="flex flex-col items-center text-white text-[11px] font-normal"
        @click="appStore.withdrawModal = true"
      >
        <VaIcon name="money_off" class="w-5 h-5 mb-0.5" />
        <span>Saque</span>
      </button>

      <!-- Espaço reservado para botão central -->
      <div class="w-14"></div>

      <!-- Depósito -->
      <button
        class="flex flex-col items-center text-white text-[11px] font-normal"
        @click="abrirNovaCota"
      >
        <VaIcon name="attach_money" class="w-5 h-5 mb-0.5" />
        <span>Ação</span>
      </button>

      <!-- Sair -->
      <button
        class="flex flex-col items-center text-white text-[11px] font-normal"
        @click="logout"
      >
        <VaIcon name="logout" class="w-5 h-5 mb-0.5" />
        <span>Sair</span>
      </button>
    </div>

    <!-- Botão central flutuante (Home) -->
    <div
      class="absolute left-1/2 transform -translate-x-1/2 -top-6 z-50 pointer-events-auto"
    >
      <button
        class="bg-[#F97316] w-14 h-14 rounded-full border-4 border-[#0C1020] flex items-center justify-center shadow-lg hover:scale-105 transition"
        @click="home"
      >
        <VaIcon name="home" class="w-6 h-6 text-white" />
      </button>
    </div>
  </div>

  <CotaModal />
  <WithdrawModal />
  <DepositModal />

  <OnboardingVideoModal
    v-model="showVideoModal"
    video-url="https://www.youtube.com/embed/YwQ6TyZc0Lc"
    @close="fecharVideoOnboarding"
  />
</template>
